import {
  AIRecommendation,
  DataVersion,
  Escalation,
  Location,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { ESCALATION_OPEN_RESOLUTION } from '../constants';
import { EscalationState } from '../types';
import AlarmEscalationBanner from './AlarmEscalationBanner';
import ScanEscalationBanner from './ScanEscalationBanner';
import { SiblingEscalationBanner } from './SiblingEscalationBanner';

interface Props {
  product: UnifiedMonitoringProduct;
  alarmGroupId: string;
  location: Location;
  escalation?: Escalation;
  scanEscalationState?: EscalationState;
  resolution?: string;
  siblingEscalations?: EscalationState[];
  onResolveEscalation?: (
    comment?: string,
    reason?: string,
    category?: string
  ) => void;
  addTemporaryResolvedState?: (alarmGroupId: string) => void;
  cameraNames?: string[];
  aiOperatorRep?: AIRecommendation;
  /** Used to address sibling escalations in the right UM bucket. */
  dataVersion?: DataVersion;
}

export function EscalationBannerContainer({
  product,
  alarmGroupId,
  location,
  escalation,
  scanEscalationState,
  resolution,
  siblingEscalations,
  onResolveEscalation,
  addTemporaryResolvedState,
  cameraNames,
  aiOperatorRep,
  dataVersion,
}: Props) {
  // Shown whenever there is one, the alarm's own status notwithstanding: an
  // incident belongs to the scope, not to the alarm that happened to open it,
  // so anyone in scope can still close it. `escalation` is already narrowed to
  // this group by AlarmGroupContainer, which is the single owner of that rule.
  //
  // #1379 briefly suppressed this on a resolved alarm to keep a sibling's
  // escalation off it. That distinction does not survive dedup -- one incident
  // per (source group, alarm type) means the sibling's incident *is* this
  // alarm's -- and re-deriving ownership here could only ever agree with the
  // container. Matches v2, which has never suppressed it.
  const showRemoteGuardingBanner =
    (product === UnifiedMonitoringProduct.REMOTE_GUARDING ||
      product === UnifiedMonitoringProduct.ENTERPRISE) &&
    escalation;

  // Scan product shows alarm escalation banner only when resolution is ESCALATION_OPEN
  const showScanAlarmBanner =
    product === UnifiedMonitoringProduct.SCAN &&
    resolution === ESCALATION_OPEN_RESOLUTION &&
    escalation;

  // Scan product shows scan escalation banner when scanEscalationState exists
  const showScanEscalationBanner =
    product === UnifiedMonitoringProduct.SCAN &&
    scanEscalationState &&
    onResolveEscalation;

  // Sibling escalations -- other groups escalated at the same location -- show
  // for every product now that RG receives the location's full list too, and v3
  // scoping means several can be active at once.
  //
  // The main banner and this list are derived by separate paths, so they can
  // disagree about a given incident; an incident shown in both places is never
  // right, so drop it here.
  const siblingsToShow = (siblingEscalations ?? []).filter(
    (sibling) => sibling.escalationId !== escalation?.id
  );
  const showSiblingBanner = siblingsToShow.length > 0;

  return (
    <>
      {showRemoteGuardingBanner && (
        <AlarmEscalationBanner
          alarmId={alarmGroupId}
          escalation={escalation}
          addTemporaryResolvedState={addTemporaryResolvedState}
          location={location}
          cameraNames={cameraNames}
          onResolveEscalation={onResolveEscalation}
          product={product}
          aiOperatorRep={aiOperatorRep}
        />
      )}

      {showScanAlarmBanner && (
        <AlarmEscalationBanner
          alarmId={alarmGroupId}
          escalation={escalation}
          location={location}
          product={product}
          addTemporaryResolvedState={addTemporaryResolvedState}
          cameraNames={cameraNames}
          aiOperatorRep={aiOperatorRep}
        />
      )}

      {showScanEscalationBanner && (
        <ScanEscalationBanner
          escalationState={scanEscalationState}
          onResolveEscalation={onResolveEscalation}
          alarmId={alarmGroupId}
          location={location}
          cameraNames={cameraNames}
          aiOperatorRep={aiOperatorRep}
        />
      )}

      {showSiblingBanner && (
        <SiblingEscalationBanner
          siblingEscalations={siblingsToShow}
          timezone={location.timezone ?? 'UTC'}
          locationName={location.name}
          product={product}
          dataVersion={dataVersion}
        />
      )}
    </>
  );
}
